import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { InputLabel } from '@/components/InputLabel';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { cn } from '@/lib/utils';

import { bookingTime, minutesFromMidnight } from '@/helpers';

type SlotPickerProps = {
  label: string;
  slots: { starts_at: string }[];
  value: string | null;
  isLoading: boolean;
  placeholder?: ReactNode;
  isInvalid?: boolean;
  isRequired?: boolean;
  onChange: (startsAt: string) => void;
};

export const SlotPicker = (props: SlotPickerProps) => {
  const {
    label,
    slots,
    value,
    isLoading,
    placeholder,
    isInvalid,
    isRequired,
    onChange,
  } = props;
  const { t } = useTranslation();

  // A clicked calendar cell gives a bare local timestamp, the slots a zoned one — match the clock.
  const selectedMinute = value ? minutesFromMidnight(value) : null;

  return (
    <div className="grid gap-2">
      <InputLabel
        label={label}
        isInvalid={isInvalid}
        isRequired={isRequired}
      />
      <div
        className={cn(
          'rounded-md border-2 p-3',
          'bg-card border',
          isInvalid ? 'border-destructive' : 'border-input',
        )}
      >
        {isLoading && <Skeleton className="h-8 w-full" />}

        {!isLoading && !!placeholder && (
          <p className="text-muted-foreground text-sm">{placeholder}</p>
        )}

        {!isLoading && !placeholder && slots.length === 0 && (
          <p className="text-muted-foreground text-sm">
            {t('No free slots for this date')}
          </p>
        )}

        {!isLoading && !placeholder && slots.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {slots.map((slot) => (
              <Button
                key={slot.starts_at}
                type="button"
                variant={
                  selectedMinute === minutesFromMidnight(slot.starts_at)
                    ? 'default'
                    : 'outline'
                }
                className="min-w-16"
                onClick={() => onChange(slot.starts_at)}
              >
                {bookingTime(slot.starts_at)}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
